import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { useUserContext } from '../../contexts/UserContext';
import EventFormInput, { InputType } from '../EventForm/EventFormInput';
import ActionButton from '../buttons/ActionButton';
import { ModalClose, ModalContainer, Overlay, Title } from './CommonModal';
import { stob64 } from '../../util/stringutils';
import AdditionalGuestsPicker from '../rsvp/AdditionalGuestPicker';
import NotificationPreferenceSelector from '../rsvp/NotificationPreferenceSelector';
import useInviteeUuidStorage from '../../hooks/useInviteeUuidStorage';
import { stripPlusOne } from '../../lib/phone';
import { IUEvent } from '../../lib/event';
import EditableAnswer from '../questions/EditableAnswer';

import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';
import CloseIcon from 'Common/src/components/icons/CloseIcon';

import {
  TAppElkAttendeeRole,
  TAppElkAttendeeStatus,
  TAppElkEventAnswer,
  TAppElkInvitee,
  TAppElkNotificationPreference
} from 'TProtocol/prototypes/events/messages';

const RSVPModal = ({ close, event, inviteeInput, eventId, clickedYes, clickedChangeResponse, hasToken }: {
  close: () => void,
  event: IUEvent | undefined,
  inviteeInput?: TAppElkInvitee | undefined,
  eventId: string | undefined,
  clickedYes?: boolean,
  clickedChangeResponse?: boolean,
  hasToken: boolean
}) => {
  const navigate = useNavigate();
  const userContext = useUserContext();
  const userId = userContext.id;
  const { setPhone, setQuestionResponse } = useInviteeUuidStorage();
  const formRef = useRef<HTMLFormElement>(null);

  const [invitee, setInvitee] = useState<TAppElkInvitee | undefined>(inviteeInput);
  const [phoneNum, setPhoneNum] = useState<string>('');
  const [userName, setUserName] = useState<string>();
  const [invalidPhone, setInvalidPhone] = useState(false);
  const [buttonText, setButtonText] = useState<string>('Confirm');

  const [message, setMessage] = useState<string>(invitee?.rsvpMessage ?? '');
  const [hasChanged, setHasChanged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isYesSelected = invitee?.rsvpStatus === TAppElkAttendeeStatus.YES;
  const isNoSelected = invitee?.rsvpStatus === TAppElkAttendeeStatus.NO;

  const { getInviteeUuid } = useInviteeUuidStorage();
  const inviteeUuid = getInviteeUuid(eventId);
  const enableUnauthedRsvp = true;

  useEffect(() => {
    if (userContext.isLoggedIn()) {
      const phone = userContext.phoneNumber;
      const name = userContext.name;

      if (phone) {
        setPhoneNum(stripPlusOne(phone));
      }
      if (name) {
        setUserName(name);
      } else if (invitee?.name) {
        setUserName(invitee.name);
      }
    }

  }, [userContext.phoneNumber, userContext.name, invitee?.name]);

  useEffect(() => {
    let inviteeToSet: undefined | TAppElkInvitee;

    if (inviteeUuid === undefined && userId !== undefined) {
      const filteredAttendees: TAppElkInvitee | undefined = event?.attendees.find(
        (attendee) => attendee?.userId === userId);
      if (filteredAttendees !== undefined) {
        inviteeToSet = filteredAttendees;
      }
    } else if (inviteeUuid !== undefined) {
      inviteeToSet = event?.attendees.find((attendee) => ((attendee?.inviteeId === inviteeUuid)));
    }

    if (inviteeToSet !== undefined) {
      if (userName !== '' && (inviteeToSet.name === undefined || inviteeToSet.name === '')) {
        inviteeToSet.name = userName;
      }
      if (inviteeToSet.notificationPreference === undefined) {
        inviteeToSet.notificationPreference = TAppElkNotificationPreference.PHONE;
      }
      setHasChanged(true);
      setInvitee(new TAppElkInvitee(inviteeToSet));
      setMessage(invitee?.rsvpMessage ?? '');
    } else {
      // This should only happen if the user is joining an open invite
      setHasChanged(true);
      setInvitee(new TAppElkInvitee({
        inviteeId: uuidv4(),
        name: userName,
        rsvpMessage: '',
        additionalGuestCount: 0,
        role: TAppElkAttendeeRole.ATTENDEE,
        // the bug is here
        rsvpStatus: clickedYes ? TAppElkAttendeeStatus.YES : TAppElkAttendeeStatus.NO
      }));
    }
  }, [event?.attendees]);

  useEffect(() => {
    if (clickedYes) {
      setButtonText('Confirm');
    } else if (clickedChangeResponse) {
      if (isNoSelected) {
        setButtonText('Decline');
      } else {
        setButtonText('Confirm');
      }
    } else {
      setButtonText('Decline');
    }
  }, [invitee]);

  if (invitee === undefined) {
    return null;
  }

  const additionalGuestCount = invitee.additionalGuestCount;

  const handleImIn = async () => {
    const numGuests = additionalGuestCount;
    let messageSlot: string;
    if (message === '') {
      messageSlot = 'm';
    } else {
      messageSlot = message;
    }

    const encodedMessage = stob64(messageSlot);
    const encodedName = stob64(invitee.name ?? '');
    const encodedEmail = stob64(invitee.email ?? '');

    let notificationPreferenceString;
    if (invitee.notificationPreference === TAppElkNotificationPreference.EMAIL) {
      notificationPreferenceString = 'email';
    } else if (invitee.notificationPreference === TAppElkNotificationPreference.BOTH) {
      notificationPreferenceString = 'both';
    } else {
      notificationPreferenceString = 'phone';
    }

    setSubmitting(true);

    //TODO: ensure title is present in all cases
    const encodedTitle = stob64(event?.title ?? '');
    if (!userContext.isLoggedIn() && hasToken && enableUnauthedRsvp) {
      navigate(
        `/event/updateemail/action/${eventId}?r=yes&em=${encodedMessage}&ng=${numGuests}&et=${encodedTitle}&en=${encodedName}&np=${notificationPreferenceString}&ee=${encodedEmail}`,
        {
          replace: true,
          state: {
            showPhotosPromo: true
          }
        });
    } else {
      navigate(
        `/event/rsvp/action/${eventId}?r=yes&em=${encodedMessage}&ng=${numGuests}&et=${encodedTitle}&en=${encodedName}&np=${notificationPreferenceString}&iu=${invitee.inviteeId}&ee=${encodedEmail}`,
        { replace: true });
    }
  };

  const handleImOut = async () => {
    const encodedMessage = stob64(message);
    const encodedName = stob64(invitee.name ?? '');
    const encodedEmail = stob64(invitee.email ?? '');
    const encodedTitle = stob64(event?.title ?? '');

    let notificationPreferenceString;
    if (invitee.notificationPreference === TAppElkNotificationPreference.EMAIL) {
      notificationPreferenceString = 'email';
    } else if (invitee.notificationPreference === TAppElkNotificationPreference.BOTH) {
      notificationPreferenceString = 'both';
    } else {
      notificationPreferenceString = 'phone';
    }

    setSubmitting(true);

    if (!userContext.isLoggedIn() && hasToken && enableUnauthedRsvp) {
      navigate(
        `/event/updateemail/action/${eventId}?r=no&em=${encodedMessage}&et=${encodedTitle}&en=${encodedName}&iu=${invitee.inviteeId}&ee=${encodedEmail}`,
        { replace: true });
    } else {
      navigate(
        `/event/rsvp/action/${eventId}?r=no&em=${encodedMessage}&ng=${additionalGuestCount}&et=${encodedTitle}&en=${encodedName}&np=${notificationPreferenceString}&iu=${invitee.inviteeId}&ee=${encodedEmail}`,
        { replace: true });
    }

  };

  const onModalClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const onMessageChange = (message: string) => {
    setMessage(message);
    invitee.rsvpMessage = message;
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);
  };

  const onPhoneChange = (phoneNumber: string) => {
    setPhoneNum(phoneNumber);
    setPhone(phoneNumber.replace(/[^0-9]/g, ''));
    setHasChanged(true);
  };

  const onEmailChange = (emailAddress: string) => {
    invitee.email = emailAddress;
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);
  };

  const onNotificationPreferenceChange = (preference: TAppElkNotificationPreference) => {
    invitee.notificationPreference = preference;
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);
  };

  const onAnswerChange = (invitee: TAppElkInvitee, answer: TAppElkEventAnswer) => {
    setQuestionResponse(eventId ?? '', answer.questionUuid, answer.answer);
    const existingAnswer = invitee.answers?.find((ans) => ans.questionUuid === answer.questionUuid);
    if (existingAnswer) {
      invitee.answers = invitee.answers?.map((ans) =>
        ans.questionUuid == answer.questionUuid ? ans = answer : ans
      );
    } else {
      let concatenatedAnswers;
      if (invitee.answers) {
        // Use the spread operator to concatenate arrays
        concatenatedAnswers = [...invitee.answers, answer];
      } else {
        concatenatedAnswers = [answer];
      }
      invitee.answers = concatenatedAnswers;
    }
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);
  };

  const handleYesClick = () => {
    invitee.rsvpStatus = TAppElkAttendeeStatus.YES;
    setInvitee(new TAppElkInvitee(invitee));
    setButtonText('Confirm');
    setHasChanged(true);
  };

  const handleNoClick = () => {
    invitee.rsvpStatus = TAppElkAttendeeStatus.NO;
    invitee.additionalGuestCount = 0;
    setMessage('');
    setButtonText('Decline');
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);
  };

  const validateForm = () => {
    const simplePhone = phoneNum.replace(/[^0-9]/g, '');
    if (simplePhone.length > 7 || invitee.notificationPreference === TAppElkNotificationPreference.EMAIL) {
      // Cursory check.  A full check for validity will be done on the login page, once we've loaded libphonenumber-js
      setInvalidPhone(false);
      return true;
    }
    setInvalidPhone(true);
    return false;
  };

  const onConfirmClick = () => {
    if ((clickedChangeResponse && isNoSelected) || (!clickedChangeResponse && !clickedYes)) {
      void handleImOut();
      return;
    }

    const validForm = validateForm();
    const validHtmlForm = formRef.current?.reportValidity();
    if (!validForm || !validHtmlForm) {
      return;
    }

    void handleImIn();
    // updateTextPermission();
  };

  const setGuestCount = (count: number) => {
    invitee.additionalGuestCount = count;
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);

  };

  const onNameChange = (name: string) => {
    invitee.name = name;
    setInvitee(new TAppElkInvitee(invitee));
    setHasChanged(true);

  };

  const onClose = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[RSVPModal] Closed modal'
    });

    close();
  }

  return (
    <>
      <Overlay onClick={onClose}>
        <ModalContainer onClick={onModalClick}>
          <ModalClose onClick={onClose}><CloseIcon/></ModalClose>
          <RSVPModalContainer>
            <RSVPForm ref={formRef}>
              {
                !clickedChangeResponse && clickedYes ? <Title> {'I\'m in!'}</Title> : !clickedChangeResponse ?
                  <Title>{'Can\'t make it!'}</Title> : <Title>{'Your response'}</Title>
              }
              {clickedChangeResponse ? <ButtonGroup>
                <Pill $active={isYesSelected} onClick={handleYesClick}>
                  {'I\'m in!'}
                </Pill>
                <Pill $active={isNoSelected} onClick={handleNoClick}>
                  {'Can\'t make it!'}
                </Pill>
              </ButtonGroup> : <></>}

              <EventFormInput
                label="Who are you RSVPing for?"
                value={invitee.name ?? ''}
                onChange={onNameChange}
                color={'var(--shine-text-color)'}
              />

              {clickedYes || (clickedChangeResponse && isYesSelected) ? <NumberOfResponses>
                <AdditionalGuestsPicker additionalGuestCount={additionalGuestCount}
                                        setGuestCount={setGuestCount}></AdditionalGuestsPicker>
              </NumberOfResponses> : <></>}

              {clickedYes || (clickedChangeResponse && isYesSelected) ? <NotificationPreferenceSelector
                value={invitee.notificationPreference ?? TAppElkNotificationPreference.PHONE}
                onChange={onNotificationPreferenceChange}
              /> : <></>}

              {
                ((invitee.notificationPreference === undefined && clickedYes) || invitee.notificationPreference === TAppElkNotificationPreference.BOTH || invitee.notificationPreference === TAppElkNotificationPreference.PHONE) ?
                  <EventFormInput
                    //icon={<PlusOne>+1</PlusOne>}
                    required
                    inputType={InputType.Phone}
                    label="Phone"
                    value={phoneNum}
                    onChange={onPhoneChange}
                    color={'var(--shine-text-color)'}
                    error={invalidPhone ? 'Please provide a valid phone number.' : undefined}
                  />
                  : <></>
              }
              {
                (invitee.notificationPreference === TAppElkNotificationPreference.EMAIL || invitee.notificationPreference === TAppElkNotificationPreference.BOTH) ?
                  <EventFormInput
                    required
                    label="Email"
                    value={invitee.email ?? userContext.getEmail() ?? ''}
                    onChange={onEmailChange}
                    color={'var(--shine-text-color)'}
                  /> : <></>
              }
              <EventFormInput
                placeholder=""
                event={event}
                label="Message for the host?"
                value={message}
                maxLength={1024}
                onChange={(value) => onMessageChange(value)}
                inputType={InputType.Description}
                optionNumber={0}
                color={'var(--shine-text-color)'}
              />

              {(clickedYes || (clickedChangeResponse && isYesSelected)) ? event?.questions.map(
                (question) => <EditableAnswer
                  event={event}
                  question={question}
                  invitee={invitee}
                  key={question.questionUuid}
                  onAnswerChange={onAnswerChange}
                />) : <></>}

              <ActionButton type="button" onClick={onConfirmClick} overrideColor={false}
                            disabled={submitting || !hasChanged}>{buttonText}</ActionButton>
            </RSVPForm>
          </RSVPModalContainer>
        </ModalContainer>
      </Overlay>
    </>
  );
};

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding-bottom: 20px;
`;

const RSVPModalContainer = styled.div`
  min-height: 525px;
  width: 100%;
  max-height: 80vh;
  overflow-y: scroll;
`;

const RSVPForm = styled.form`
  margin: 5px;
`;

const Pill = styled.div<{ $active: boolean }>`
  display: flex;
  padding: 8px 12px;
  cursor: pointer;
  border: ${props => (props.$active ? '0px' : '0px')};
  border-radius: 30px 30px 30px 30px;
  font-weight: ${props => (props.$active ? 'bold' : 'normal')};
  color: ${props => (props.$active ? 'var(--shine-system-button-text-color)' : '#fff')};
  font-size: 18px;
  margin-right: 10px;
  background-color: ${props => (props.$active ? 'var(--shine-highlight-color)' : 'rgb(0, 0, 0, 0.2)')};)}

`;

const NumberOfResponses = styled.div`

`;

const PlusOne = styled.div`
  font-size: 16px;
  color: white;
`;

const CenteredLabel = styled.div`
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 18px;
  font-weight: 300;
  margin-top: 10px;
  margin-bottom: 5px;
`;
export default RSVPModal;
